/**
 * Matriz de previsão de pagamentos a fornecedores: uma linha por fornecedor,
 * uma coluna por mês (corrente → último pagamento previsto), mais a coluna
 * "Sem lançamento" (parcela vencida sem lançamento vinculado ao pedido — é
 * pendência de conciliação, não dívida: o sistema não tem baixa) e a linha de
 * total. Mesmo padrão sticky da matriz de cartões (CreditCardProjectionMatrix).
 *
 * Cada linha expande para os pagamentos que a compõem, por mês: parcela do
 * pedido ("só no pedido"), lançamento a pagar ou já pago.
 */
import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import type { SupplierForecast, SupplierForecastGroup, SupplierForecastItem } from '@/types/financialReports';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import { monthLabelPtBR } from './financialReportsUtils';
import {
  FLAG_STYLES,
  dueDateShiftLabel,
  forecastCellNote,
  forecastHasOverdue,
  forecastSourceLabel,
  sortedFlags,
  supplierKey,
} from './supplierReport';

/** Coluna do fornecedor fixa à esquerda no scroll horizontal (bg por célula). */
const stickyCellCls = 'sticky left-0 z-10 py-2 px-3 min-w-[150px] sm:min-w-[260px]';

const MoneyCell: React.FC<{ value: number; note?: string; bold?: boolean; danger?: boolean }> = ({
  value,
  note,
  bold,
  danger,
}) => {
  if (value === 0 && !note) {
    return <td className="text-right py-2 px-3 text-slate-300">–</td>;
  }
  return (
    <td
      className={clsx(
        'text-right py-2 px-3 whitespace-nowrap',
        bold && 'font-semibold',
        danger ? 'text-red-600' : 'text-slate-800',
      )}
    >
      {formatCurrencyBRL(value)}
      {note && <span className="block text-[10px] font-normal text-slate-400">{note}</span>}
    </td>
  );
};

const ItemRow: React.FC<{ item: SupplierForecastItem; months: string[] }> = ({ item, months }) => {
  const monthLabel = item.monthIndex === -1 ? 'Sem lanç.' : monthLabelPtBR(months[item.monthIndex] ?? '');
  const source = forecastSourceLabel(item);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 border-b border-slate-100 last:border-0 text-xs">
      <span className={clsx('w-20 shrink-0 font-medium', item.monthIndex === -1 ? 'text-red-600' : 'text-slate-500')}>
        {monthLabel}
      </span>
      <span className="text-slate-700 min-w-0 flex-1">
        {item.documentId != null && item.source === 'parcela' ? (
          <RouterLink to={`/purchase-orders/${item.documentId}`} className="text-emerald-600 font-medium hover:underline">
            {item.label}
          </RouterLink>
        ) : (
          item.label
        )}
        {item.documentLabel && item.source === 'lancamento' && (
          <RouterLink
            to={`/purchase-orders/${item.documentId}`}
            className="ml-2 text-emerald-600 hover:underline"
          >
            {item.documentLabel}
          </RouterLink>
        )}
      </span>
      <span
        className="text-slate-400 whitespace-nowrap"
        title={dueDateShiftLabel(item.originalDate, item.date, item.dateShift, formatDateBR) || undefined}
      >
        {formatDateBR(item.date)}
        {item.originalDate && <span className="text-amber-600"> (pedido: {formatDateBR(item.originalDate)})</span>}
      </span>
      <span
        className={clsx(
          'rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap',
          item.source === 'parcela'
            ? 'bg-amber-50 text-amber-700'
            : item.realized
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-slate-100 text-slate-600',
        )}
      >
        {source}
      </span>
      {sortedFlags(item.flags).map(f => (
        <span
          key={f}
          className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap', FLAG_STYLES[f].cls)}
          title={FLAG_STYLES[f].title}
        >
          {FLAG_STYLES[f].label}
        </span>
      ))}
      <span className="ml-auto font-medium text-slate-800 whitespace-nowrap">{formatCurrencyBRL(item.amount)}</span>
    </div>
  );
};

export const SupplierForecastMatrix: React.FC<{ data: SupplierForecast }> = ({ data }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (data.months.length === 0 || data.groups.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        Nenhum pagamento a fornecedor previsto para as próximas competências.
      </p>
    );
  }

  const hasOverdue = forecastHasOverdue(data.groups);
  const columns = data.months.length + (hasOverdue ? 1 : 0) + 2;

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderGroup = (g: SupplierForecastGroup) => {
    const key = supplierKey(g.providerId);
    const open = expanded.has(key);
    const items = [...g.items].sort((a, b) => a.monthIndex - b.monthIndex || a.date.localeCompare(b.date));
    return (
      <React.Fragment key={key}>
        <tr className="border-b border-slate-100 hover:bg-slate-50">
          <td className={clsx(stickyCellCls, 'bg-white')}>
            <button
              type="button"
              onClick={() => toggle(key)}
              className="inline-flex items-center gap-1.5 text-left text-slate-800 hover:text-emerald-700"
              aria-expanded={open}
            >
              {open ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
              <span>{g.providerName}</span>
            </button>
          </td>
          {hasOverdue && <MoneyCell value={g.overdue} danger bold={g.overdue > 0} />}
          {g.monthAmounts.map((v, i) => (
            <MoneyCell
              key={data.months[i]}
              value={v}
              note={forecastCellNote(v, g.monthOrderOnly[i], g.monthRealized[i], formatCurrencyBRL) || undefined}
            />
          ))}
          <MoneyCell value={g.total} bold />
        </tr>
        {open && (
          <tr className="border-b border-slate-100 bg-slate-50/60">
            <td colSpan={columns} className="px-3 sm:px-8 py-2">
              {items.length === 0 ? (
                <p className="text-xs text-slate-400">Sem pagamentos previstos.</p>
              ) : (
                items.map(item => (
                  <ItemRow key={`${item.source}-${item.transactionId ?? 't'}-${item.label}-${item.date}`} item={item} months={data.months} />
                ))
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className={clsx(stickyCellCls, 'bg-white text-left font-semibold text-slate-600')}>Fornecedor</th>
            {hasOverdue && (
              <th
                className="text-right py-2 px-3 font-semibold text-amber-700 whitespace-nowrap"
                title="Parcelas de pedido vencidas antes do mês corrente sem nenhum lançamento vinculado ao pedido. Não é dívida: o sistema não tem baixa, então o pagamento pode existir no Conta Corrente sem estar amarrado ao pedido. Resolva vinculando o lançamento ao PC no painel do mês."
              >
                Sem lançamento
                <span className="block text-[10px] font-normal text-amber-600">parcelas vencidas</span>
              </th>
            )}
            {data.months.map(m => (
              <th key={m} className="text-right py-2 px-3 font-semibold text-slate-600 whitespace-nowrap">
                {monthLabelPtBR(m)}
              </th>
            ))}
            <th className="text-right py-2 px-3 font-bold text-slate-700 whitespace-nowrap">Total</th>
          </tr>
        </thead>
        <tbody>{data.groups.map(renderGroup)}</tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold border-t border-slate-200">
            <td className={clsx(stickyCellCls, 'bg-slate-50 text-slate-700')}>Total do mês</td>
            {hasOverdue && <MoneyCell value={data.overdueTotal} danger bold />}
            {data.monthTotals.map((v, i) => (
              <MoneyCell key={data.months[i]} value={v} bold />
            ))}
            <MoneyCell value={data.total} bold />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SupplierForecastMatrix;
